import {
  ChakraBox as Box,
  ChakraDivider as Divider,
  ChakraFlex as Flex,
  ChakraHeading as Heading,
  ChakraStack as Stack,
} from "@airhelp/react-ui-components";
import { stickyBottomBarFirstTriggerStyles } from "components/StickyBottomBar";
import StickyBottomBarTrigger from "components/StickyBottomBar/StickyBottomBarTrigger";
import { DataContextProvider } from "contexts/DataContext";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { MobileSideMenu } from "components/MobileSideMenu";
import { ModalContextProvider } from "contexts/ModalContext";
import { StickyBottomBarContextProvider } from "contexts/StickyBottomBarContext";
import CustomHead from "modules/common/components/CustomHead";
import Footer from "modules/common/components/Footer";
import { Grid } from "modules/common/components/Grid";
import { CheckCompensationButton, HeaderNav, MenuItems } from "modules/common/components/HeaderNav";
import { PressLogos } from "modules/common/components/PressLogos";
import { renderDynamicComponent } from "modules/storyblok/DynamicComponent";
import { useRouter } from "next/router";
import getDomainFromLanguage from "utils/getDomainFromLanguage";
import { getStoryLastUpdatedDate } from "utils/getStoryLastUpdatedDate";
import { useTopMenu } from "hooks/useMenu";
import StickyBottomBarAdapter from "../../adapters/StickyBottomBar";
import { BlogSubscribeBox, SocialLinksPanel } from "../../components";
import type { BlogPostTemplateProps } from "./BlogPostTemplate.d";
import { BlogFeaturedImage } from "./BlogFeaturedImage";

const BlogPostTemplate = ({
  story,
  cmsLanguagesData,
  serverSideStoryblokPages,
  whiteListDomains,
  googleOptimizeExperimentIds,
  nofollowUrls,
}: BlogPostTemplateProps) => {
  const {
    name,
    content: {
      metaTitle,
      metaDescription,
      author,
      featuredImage,
      body,
      stickyBottomBarHeading,
      hideStickyBottomBar,
      stickyBottomBarVariant,
    },
  } = story;

  const { query } = useRouter();
  const language = useLanguage();
  const translations = useTranslations();
  const topMenu = useTopMenu(language);
  const lastUpdatedDate = getStoryLastUpdatedDate(story, language);
  const domain = getDomainFromLanguage(language);
  const url = `${domain}/${query.language}/blog/${query.slug[1]}`;

  const blogpostCaption = author
    ? `${translations("by_author_name")} ${author}・${translations("last_updated")} ${lastUpdatedDate}`
    : `${translations("last_updated")} ${lastUpdatedDate}`;

  return (
    <DataContextProvider
      data={{ cmsLanguagesData, serverSideStoryblokPages, whiteListDomains, googleOptimizeExperimentIds, nofollowUrls }}
    >
      <CustomHead title={metaTitle} description={metaDescription} />
      <MobileSideMenu>
        <ModalContextProvider>
          <HeaderNav menuItems={<MenuItems menu={topMenu} />} headerButtons={<CheckCompensationButton />} />
          <Grid pt={32} as="main">
            <Box gridColumn={{ base: "initial", md: "2 / span 10", lg: "3 / span 8" }}>
              <BlogFeaturedImage src={featuredImage} alt={name} />
              <Heading variant="h1" as="h1" mb={6}>
                {name}
              </Heading>
              <Box mb={10} color="greyscale.500" fontSize="lg">
                {blogpostCaption}
              </Box>
              <Stack spacing={6}>
                <StickyBottomBarTrigger type="first" styles={stickyBottomBarFirstTriggerStyles} />
                {renderDynamicComponent(body)}
              </Stack>
              <BlogSubscribeBox />
              <Divider mb={16} pt={16} />
              <Flex direction={{ base: "column", lg: "row" }} alignItems="center" justifyContent="space-between">
                <Heading variant="h3" as="h3" mb={{ base: 7, lg: 0 }}>
                  {translations("share_it_with_your_friends")}
                </Heading>
                <SocialLinksPanel url={url} title={name} />
              </Flex>
              <SocialLinksPanel
                direction="column"
                url={url}
                title={name}
                pos="fixed"
                top={28}
                left={16}
                display={{ base: "none", lg: "flex" }}
              />
            </Box>
          </Grid>
          <PressLogos />
          <Footer />
        </ModalContextProvider>
      </MobileSideMenu>
      <StickyBottomBarAdapter
        showStickyBottomBar={!hideStickyBottomBar}
        stickyBottomBarVariant={stickyBottomBarVariant}
        stickyBottomBarHeading={stickyBottomBarHeading}
      />
    </DataContextProvider>
  );
};

export const BlogPostTemplateAdapter = (props: BlogPostTemplateProps) => (
  <StickyBottomBarContextProvider threshold={0.4}>
    <BlogPostTemplate {...props} />
  </StickyBottomBarContextProvider>
);
